import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Button,
  Divider,
  Dropdown,
  DropdownItem,
  DropdownList,
  EmptyState,
  EmptyStateBody,
  MenuToggle,
  MenuToggleElement,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  ModalVariant,
  NotificationDrawer,
  NotificationDrawerBody,
  NotificationDrawerHeader,
  NotificationDrawerList,
  NotificationDrawerListItem,
  NotificationDrawerListItemBody,
  NotificationDrawerListItemHeader,
  Spinner
} from '@patternfly/react-core';
import { BellIcon, EllipsisVIcon, ExclamationCircleIcon } from '@patternfly/react-icons';
import { formatDistanceToNow } from 'date-fns';
import { KlassApi } from '@app/api';
import { NOTIFICATION_SEVERITY } from '@app/model/Enums';
import { Notification } from '@app/model/Notification';
import { useKlassStore } from '@app/store/entityStore';
import { useNotificationStore } from '@app/store/notificationStore';
import { ddMMyyyyHHmmFormat } from '@app/utils/functions';

/**
 * Severity as PatternFly paints it. The service has three levels and the drawer has five variants,
 * so `success` and `custom` are simply unused — nothing raises a notification to say something went
 * right.
 */
const VARIANTS: Record<NOTIFICATION_SEVERITY, 'danger' | 'warning' | 'info'> = {
  [NOTIFICATION_SEVERITY.ERROR]: 'danger',
  [NOTIFICATION_SEVERITY.WARNING]: 'warning',
  [NOTIFICATION_SEVERITY.INFO]: 'info'
};

const variantOf = (notification: Notification) => VARIANTS[notification.severity] ?? 'info';

/** "5 minutes ago", or the raw value if the service ever sends a date this cannot parse. */
const relativeTime = (timestamp: string): string => {
  const date = new Date(timestamp);
  return Number.isNaN(date.getTime()) ? timestamp : formatDistanceToNow(date, { addSuffix: true });
};

interface NotificationDrawerPanelProps {
  onClose: () => void;
  /** Focused when the panel opens, so the drawer is reachable from the keyboard. */
  drawerRef: React.RefObject<HTMLDivElement | null>;
}

/**
 * The bell's panel: the caller's own inbox, newest first.
 *
 * Every row here is addressed to whoever is signed in — by name, by one of their roles, or to
 * everybody — and the service decides which, on every read. So there is nothing to filter on this
 * side, and nothing here may assume the panel holds the whole inbox: it holds the pages that have
 * been asked for, while the unread count in the header speaks for all of it.
 *
 * **Dismissing hides a row for this user and nobody else.** It is a receipt written against their
 * username, never a deletion — the notification itself belongs to everybody it was addressed to.
 * That is why the row goes away here but the count other people see does not move, and why there is
 * no undo: the service offers none, deliberately.
 */
export const NotificationDrawerPanel: React.FunctionComponent<NotificationDrawerPanelProps> = ({
  onClose,
  drawerRef
}) => {
  const navigate = useNavigate();

  const items = useNotificationStore((state) => state.items);
  const unreadCount = useNotificationStore((state) => state.unreadCount);
  const isLoading = useNotificationStore((state) => state.isLoading);
  const isLoaded = useNotificationStore((state) => state.isLoaded);
  const hasMore = useNotificationStore((state) => state.hasMore);
  const error = useNotificationStore((state) => state.error);

  const { markRead, markUnread, markAllRead, dismiss, dismissAll, loadMore, load } =
    useNotificationStore.getState();

  const confirmDismissAll = async () => {
    setIsDismissingAll(true);
    try {
      await dismissAll();
    } finally {
      setIsDismissingAll(false);
      setIsDismissAllOpen(false);
    }
  };

  // Which row's kebab is open. One piece of state rather than one per row, since only one can be.
  const [openMenuId, setOpenMenuId] = React.useState<number | null>(null);
  const [isDrawerMenuOpen, setIsDrawerMenuOpen] = React.useState(false);
  const [isDismissAllOpen, setIsDismissAllOpen] = React.useState(false);
  const [isDismissingAll, setIsDismissingAll] = React.useState(false);

  /**
   * Opens what the notification is about, when that is somewhere this application can go.
   *
   * A class is the awkward one: the notification carries the numeric row id, while the class detail
   * route is addressed by offering id, so it has to be translated. From the class store when the
   * class falls inside the window it holds, and from the service when it does not — which is the
   * common case for a notification old enough to be worth chasing.
   */
  const openTarget = async (notification: Notification) => {
    const { targetEntity, targetId } = notification;
    if (!targetEntity || targetId == null) return;

    try {
      switch (targetEntity) {
        case 'INSTRUCTOR':
          navigate(`/instructors/${targetId}`);
          onClose();
          break;

        case 'KLASS': {
          const cached = useKlassStore.getState().items.find((klass) => klass.id === targetId);
          const offeringId = cached?.offeringId ?? (await KlassApi.getById(targetId))?.offeringId;
          if (offeringId) {
            navigate(`/classes/${offeringId}`);
            onClose();
          }
          break;
        }

        // Anything else — an automation message naming an entity with no detail page of its own,
        // or an entity added to the catalog since this was written. The notification still reads
        // perfectly well on its own; it just has nowhere to take you.
        default:
          break;
      }
    } catch (err) {
      // A target that cannot be resolved is not worth an error in front of the user: the class may
      // have been removed since, and the notification text says what happened either way.
      console.warn(`[Notifications] Could not open ${targetEntity} ${targetId}:`, err);
    }
  };

  const onItemClick = (notification: Notification) => {
    void markRead(notification.id);
    void openTarget(notification);
  };

  const itemActions = (notification: Notification) => (
    <Dropdown
      isOpen={openMenuId === notification.id}
      onSelect={() => setOpenMenuId(null)}
      onOpenChange={(isOpen) => setOpenMenuId(isOpen ? notification.id : null)}
      popperProps={{ position: 'right' }}
      toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
        <MenuToggle
          ref={toggleRef}
          aria-label={`Actions for notification: ${notification.title}`}
          variant="plain"
          isExpanded={openMenuId === notification.id}
          icon={<EllipsisVIcon />}
          // Without this the row's own onClick fires as well, so opening the menu would mark the
          // notification read and navigate away from the panel it just opened in.
          onClick={(event) => {
            event.stopPropagation();
            setOpenMenuId(openMenuId === notification.id ? null : notification.id);
          }}
        />
      )}
    >
      {/*
        * Every item here stops the click going any further, and none of them may forget to.
        * This menu sits inside the row, whose own onClick opens whatever the notification is
        * about — so an item that lets the event through does its job and then navigates away from
        * the panel, which is what dismissing a class notification used to do. A menu is a question
        * about the notification; the row is a request to go and look at it.
        */}
      <DropdownList>
        {notification.read ? (
          <DropdownItem
            key="unread"
            onClick={(event) => { event.stopPropagation(); void markUnread(notification.id); }}
          >
            Mark as unread
          </DropdownItem>
        ) : (
          <DropdownItem
            key="read"
            onClick={(event) => { event.stopPropagation(); void markRead(notification.id); }}
          >
            Mark as read
          </DropdownItem>
        )}
        <Divider component="li" />
        {/* Hides it for this user only — see the component comment. */}
        <DropdownItem
          key="dismiss"
          onClick={(event) => { event.stopPropagation(); void dismiss(notification.id); }}
        >
          Dismiss
        </DropdownItem>
      </DropdownList>
    </Dropdown>
  );

  const drawerActions = (
    <Dropdown
      isOpen={isDrawerMenuOpen}
      onSelect={() => setIsDrawerMenuOpen(false)}
      onOpenChange={setIsDrawerMenuOpen}
      popperProps={{ position: 'right' }}
      toggle={(toggleRef: React.Ref<MenuToggleElement>) => (
        <MenuToggle
          ref={toggleRef}
          aria-label="Notification actions"
          variant="plain"
          isExpanded={isDrawerMenuOpen}
          icon={<EllipsisVIcon />}
          onClick={() => setIsDrawerMenuOpen(!isDrawerMenuOpen)}
        />
      )}
    >
      <DropdownList>
        <DropdownItem key="read-all" isDisabled={unreadCount === 0} onClick={() => void markAllRead()}>
          Mark all as read
        </DropdownItem>
        <Divider component="li" />
        {/*
          * Asks first, unlike every other action in this panel. Dismissing is final — the service
          * offers nothing that undoes it — and this one does it to everything at once, including
          * the pages the panel has not shown yet.
          */}
        <DropdownItem
          key="dismiss-all"
          isDisabled={items.length === 0}
          onClick={() => setIsDismissAllOpen(true)}
        >
          Dismiss all
        </DropdownItem>
      </DropdownList>
    </Dropdown>
  );

  const body = () => {
    if (error && items.length === 0) {
      return (
        <EmptyState
          status="danger"
          headingLevel="h2"
          icon={ExclamationCircleIcon}
          titleText="Notifications are unavailable"
          variant="sm"
        >
          <EmptyStateBody>
            {error}
            <div style={{ marginTop: 'var(--pf-t--global--spacer--md, 16px)' }}>
              <Button variant="secondary" onClick={() => void load()}>Try again</Button>
            </div>
          </EmptyStateBody>
        </EmptyState>
      );
    }

    // Only while there is nothing to show. Once a page has loaded, paging and marking happen under
    // the list rather than replacing it.
    if (!isLoaded && isLoading) {
      return (
        <EmptyState headingLevel="h2" icon={Spinner} titleText="Loading notifications" variant="sm" />
      );
    }

    if (items.length === 0) {
      return (
        <EmptyState headingLevel="h2" icon={BellIcon} titleText="No notifications" variant="sm">
          <EmptyStateBody>There is nothing for you to catch up on.</EmptyStateBody>
        </EmptyState>
      );
    }

    return (
      <NotificationDrawerList>
        {items.map((notification) => (
          <NotificationDrawerListItem
            key={notification.id}
            variant={variantOf(notification)}
            isRead={notification.read}
            onClick={() => onItemClick(notification)}
          >
            <NotificationDrawerListItemHeader
              variant={variantOf(notification)}
              title={notification.title}
              srTitle={`${notification.severity.toLowerCase()} notification:`}
            >
              {itemActions(notification)}
            </NotificationDrawerListItemHeader>
            <NotificationDrawerListItemBody
              timestamp={
                <span title={ddMMyyyyHHmmFormat(notification.createdAt)}>
                  {relativeTime(notification.createdAt)}
                </span>
              }
            >
              {notification.message}
            </NotificationDrawerListItemBody>
          </NotificationDrawerListItem>
        ))}

        {hasMore && (
          <li style={{ padding: 'var(--pf-t--global--spacer--md, 16px)', textAlign: 'center' }}>
            <Button variant="link" isInline isLoading={isLoading} onClick={() => void loadMore()}>
              {isLoading ? 'Loading' : 'Show older notifications'}
            </Button>
          </li>
        )}
      </NotificationDrawerList>
    );
  };

  return (
    <NotificationDrawer ref={drawerRef}>
      <NotificationDrawerHeader
        title="Notifications"
        count={unreadCount}
        unreadText="unread"
        onClose={onClose}
        closeButtonAriaLabel="Close notifications"
      >
        {drawerActions}
      </NotificationDrawerHeader>
      <NotificationDrawerBody>{body()}</NotificationDrawerBody>

      <Modal
        variant={ModalVariant.small}
        isOpen={isDismissAllOpen}
        onClose={() => setIsDismissAllOpen(false)}
        aria-label="Dismiss all notifications"
      >
        <ModalHeader title="Dismiss all notifications?" titleIconVariant="warning" />
        <ModalBody>
          <p>
            This hides every notification addressed to you, including any the panel has not loaded
            yet. Nobody else&apos;s is touched.
          </p>
          <p>There is no way to bring them back.</p>
        </ModalBody>
        <ModalFooter>
          <Button
            variant="danger"
            isLoading={isDismissingAll}
            isDisabled={isDismissingAll}
            onClick={() => void confirmDismissAll()}
          >
            {isDismissingAll ? 'Dismissing' : 'Dismiss all'}
          </Button>
          <Button variant="link" isDisabled={isDismissingAll} onClick={() => setIsDismissAllOpen(false)}>
            Cancel
          </Button>
        </ModalFooter>
      </Modal>
    </NotificationDrawer>
  );
};
